
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseFileUploadOptions {
  bucket: string;
  allowedTypes?: string[];
  maxSizeBytes?: number;
  onSuccess?: (url: string, fileName: string) => void;
  onError?: (error: string) => void;
}

export const useFileUpload = ({
  bucket,
  allowedTypes = [],
  maxSizeBytes = 5 * 1024 * 1024, // 5MB default
  onSuccess,
  onError
}: UseFileUploadOptions) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFile = async (file: File, path?: string) => {
    if (!file) return null;

    // Validate file type
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      const error = `File type ${file.type} not allowed. Allowed types: ${allowedTypes.join(', ')}`;
      toast.error(error);
      onError?.(error);
      return null;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      const error = `File size too large. Maximum size: ${(maxSizeBytes / 1024 / 1024).toFixed(1)}MB`;
      toast.error(error);
      onError?.(error);
      return null;
    }

    setUploading(true);
    setProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = path || `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      setProgress(100);
      toast.success('File uploaded successfully');
      onSuccess?.(publicUrl, fileName);
      
      return { url: publicUrl, path: data.path };
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Upload failed';
      toast.error(errorMessage);
      onError?.(errorMessage);
      return null;
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const deleteFile = async (path: string) => {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);
      
      if (error) throw error;
      toast.success('File deleted successfully');
      return true;
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
      return false;
    }
  };

  return {
    uploadFile,
    deleteFile,
    uploading,
    progress
  };
};
